import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class UserService {
    /**
     * Get Current User
     * Retrieve the current user's information - if they're logged in.
     *
     * Does not require ViewUsers permission.
     * @returns ViewUser Successful Response
     * @throws ApiError
     */
    static getCurrentUser() {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/user',
        });
    }
    /**
     * Update User
     * Update certain aspects of the user.
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static updateUser(requestBody) {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/user',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update User By Id
     * Update certain aspects of the user.
     * @param userId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static updateUserById(userId, requestBody) {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/user/{user_id}',
            path: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Delete User
     * Delete the given user's account. This will delete all data
     * associated with the user, including any resources and jobs they have
     * created. Use with caution.
     * @param userId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static deleteUser(userId, requestBody) {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/user/{user_id}',
            path: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update User Pic
     * Allow the user to post a new profile picture.
     *
     * Requires authenticated user - users can only set their own profile pic.
     *
     * The picture will be cropped to be square and scaled to 1024x1024.
     *
     * The picture profile URL will always be (for now) <user_id>.png.
     * @param formData
     * @returns any Successful Response
     * @throws ApiError
     */
    static updateUserPic(formData) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/user/profile_picture',
            formData: formData,
            mediaType: 'multipart/form-data',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update User Password
     * Set the user's password. This will require the user to be logged in.
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static updateUserPassword(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/user/password',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Reset User Password
     * Support endpoint to reset a user's password.
     *
     * This is a temporary solution to allow us to have users regain access to
     * their account.
     *
     * Long term, this should be done via a reset email, which includes a link
     * that the user can use to set their password for the first time.
     * @param userId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static resetUserPassword(userId, requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/user/{user_id}/password',
            path: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Create User From Token
     * @param token
     * @returns any Successful Response
     * @throws ApiError
     */
    static createUserFromToken(token) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/user/create',
            query: {
                'token': token,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Login
     * Login endpoint for users. Expects a password request form with the
     * user's data, and returns a valid token (assuming the data is correct).
     * @param formData
     * @returns AuthResponse Successful Response
     * @throws ApiError
     */
    static login(formData) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/user/token',
            formData: formData,
            mediaType: 'application/x-www-form-urlencoded',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update User Synthesizer Task Config
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static updateUserSynthesizerTaskConfig(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/user/synth_task_config',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get All Users
     * Fetch all users if the current user is authenticated and authorized.
     * @param requestBody
     * @returns ViewUserList Successful Response
     * @throws ApiError
     */
    static getAllUsers(requestBody) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/users',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Create User
     * Create a user with the given model.
     *
     * This currently only exists here as it's currently implemented for
     * development purposes; special cases should handle first / org
     * user creation.
     * @param requestBody
     * @returns AuthResponse Successful Response
     * @throws ApiError
     */
    static createUser(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/users',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get User By Id
     * Retrieve another user's info by id.
     * @param userId
     * @param requestBody
     * @returns ViewUser Successful Response
     * @throws ApiError
     */
    static getUserById(userId, requestBody) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/users/{user_id}',
            path: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
